/**
 * Shared buyer-facing formatters for normalized listings (Milestone 2).
 */
import { formatFairPriceLabel as formatFairPriceLabelFromVm } from "../fairPriceViewModel";
import { getRiskLevelLabel } from "../vehicleRiskViewModel";
import {
  LISTING_BODY_TYPE,
  LISTING_FAIR_PRICE_LABEL,
  LISTING_FALLBACK_COPY,
  LISTING_FUEL_TYPE,
  LISTING_RISK_LEVEL,
  LISTING_TRANSMISSION,
  AVAILABILITY_STATUS,
  LISTING_STATUS,
} from "../../constants/automapListingConstants";
import {
  buildCanonicalTitleFromListing,
  DEFAULT_FALLBACK as CANONICAL_VEHICLE_TITLE_FALLBACK,
} from "./canonicalVehicleTitle";

export const UNKNOWN_ATTRIBUTE_LABEL = "Unknown";

export function formatPriceCop(priceCop, currency = "COP", options = {}) {
  if (priceCop == null || Number.isNaN(Number(priceCop))) {
    return LISTING_FALLBACK_COPY.price;
  }
  if (typeof options.formatMoney === "function") {
    return options.formatMoney(Number(priceCop), { displayCurrency: currency });
  }
  const formatted = new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 }).format(Number(priceCop));
  return `COP $${formatted}`;
}

export function formatMileageKm(mileageKm, unknownLabel = UNKNOWN_ATTRIBUTE_LABEL) {
  if (mileageKm == null || Number.isNaN(Number(mileageKm))) {
    return unknownLabel;
  }
  const km = Number(mileageKm);
  if (km >= 1000) {
    return `${Math.round(km / 1000)}k km`;
  }
  return `${km.toLocaleString("es-CO")} km`;
}

export function formatMileageKmLong(mileageKm, unknownLabel = UNKNOWN_ATTRIBUTE_LABEL) {
  if (mileageKm == null || Number.isNaN(Number(mileageKm))) {
    return unknownLabel;
  }
  return `${Number(mileageKm).toLocaleString("es-CO")} km`;
}

export function formatVehicleYear(year, unknownLabel = UNKNOWN_ATTRIBUTE_LABEL) {
  if (year == null || Number.isNaN(Number(year))) return unknownLabel;
  return String(year);
}

export function formatVehicleTitle(listing = {}, options = {}) {
  const fallback = options.fallback ?? CANONICAL_VEHICLE_TITLE_FALLBACK;
  return buildCanonicalTitleFromListing(listing, { fallback });
}

export function formatLocationLabel(listing = {}) {
  if (listing.locationLabel?.trim()) return listing.locationLabel.trim();
  const parts = [listing.neighborhood, listing.city, listing.department].filter(Boolean);
  return parts.length ? parts.join(", ") : LISTING_FALLBACK_COPY.location;
}

const TRANSMISSION_LABELS_ES = {
  [LISTING_TRANSMISSION.AUTOMATIC]: "Automática",
  [LISTING_TRANSMISSION.MANUAL]: "Mecánica",
  [LISTING_TRANSMISSION.AUTOMATED_MANUAL]: "Secuencial",
  [LISTING_TRANSMISSION.CVT]: "CVT",
  [LISTING_TRANSMISSION.UNKNOWN]: UNKNOWN_ATTRIBUTE_LABEL,
};

export function formatTransmission(transmission, unknownLabel = UNKNOWN_ATTRIBUTE_LABEL) {
  if (!transmission || transmission === LISTING_TRANSMISSION.UNKNOWN) return unknownLabel;
  if (TRANSMISSION_LABELS_ES[transmission]) return TRANSMISSION_LABELS_ES[transmission];
  return String(transmission);
}

const FUEL_LABELS_ES = {
  [LISTING_FUEL_TYPE.GASOLINE]: "Gasolina",
  [LISTING_FUEL_TYPE.DIESEL]: "Diésel",
  [LISTING_FUEL_TYPE.HYBRID]: "Híbrido",
  [LISTING_FUEL_TYPE.ELECTRIC]: "Eléctrico",
  [LISTING_FUEL_TYPE.GAS]: "Gasolina",
  [LISTING_FUEL_TYPE.UNKNOWN]: UNKNOWN_ATTRIBUTE_LABEL,
};

export function formatFuelType(fuelType, unknownLabel = UNKNOWN_ATTRIBUTE_LABEL) {
  if (!fuelType || fuelType === LISTING_FUEL_TYPE.UNKNOWN) return unknownLabel;
  if (FUEL_LABELS_ES[fuelType]) return FUEL_LABELS_ES[fuelType];
  return String(fuelType);
}

const BODY_LABELS_ES = {
  [LISTING_BODY_TYPE.SEDAN]: "Sedán",
  [LISTING_BODY_TYPE.HATCHBACK]: "Hatchback",
  [LISTING_BODY_TYPE.SUV]: "SUV",
  [LISTING_BODY_TYPE.PICKUP]: "Pickup",
  [LISTING_BODY_TYPE.VAN]: "Van",
  [LISTING_BODY_TYPE.COUPE]: "Coupé",
  [LISTING_BODY_TYPE.WAGON]: "Wagon",
  [LISTING_BODY_TYPE.OTHER]: "Otro",
  [LISTING_BODY_TYPE.UNKNOWN]: UNKNOWN_ATTRIBUTE_LABEL,
};

export function formatBodyType(bodyType, unknownLabel = UNKNOWN_ATTRIBUTE_LABEL) {
  if (!bodyType || bodyType === LISTING_BODY_TYPE.UNKNOWN) return unknownLabel;
  if (BODY_LABELS_ES[bodyType]) return BODY_LABELS_ES[bodyType];
  return String(bodyType);
}

const AVAILABILITY_LABELS_ES = {
  [AVAILABILITY_STATUS.AVAILABLE]: "Disponible",
  [AVAILABILITY_STATUS.PENDING_CONFIRMATION]: "Por confirmar",
  [AVAILABILITY_STATUS.UNAVAILABLE]: "No disponible",
  [AVAILABILITY_STATUS.SOLD]: "Vendido",
  [AVAILABILITY_STATUS.UNKNOWN]: LISTING_FALLBACK_COPY.confirmation,
};

export function formatAvailabilityStatus(status) {
  return AVAILABILITY_LABELS_ES[status] || LISTING_FALLBACK_COPY.confirmation;
}

const STATUS_LABELS_ES = {
  [LISTING_STATUS.ACTIVE]: "Activo",
  [LISTING_STATUS.SOLD]: "Vendido",
  [LISTING_STATUS.PAUSED]: "Pausado",
  [LISTING_STATUS.ARCHIVED]: "Archivado",
  [LISTING_STATUS.REJECTED]: "Rechazado",
  [LISTING_STATUS.PENDING_REVIEW]: "En revisión",
  [LISTING_STATUS.DRAFT]: "Borrador",
  [LISTING_STATUS.UNKNOWN]: "—",
};

export function formatListingStatus(status) {
  return STATUS_LABELS_ES[status] || "—";
}

export function formatLastConfirmedAt(date) {
  if (!date) return LISTING_FALLBACK_COPY.confirmation;
  const ts = new Date(date).getTime();
  if (Number.isNaN(ts)) return LISTING_FALLBACK_COPY.confirmation;
  const hours = Math.round((Date.now() - ts) / 3_600_000);
  if (hours <= 1) return "Confirmado hoy";
  if (hours < 24) return `Confirmado hace ${hours}h`;
  const days = Math.round(hours / 24);
  if (days === 1) return "Confirmado ayer";
  if (days < 7) return `Confirmado hace ${days} días`;
  return new Date(date).toLocaleDateString("es-CO");
}

export const FRESHNESS_RECENT_DAYS = 7;

/**
 * Buyer-facing freshness label for search cards (Milestone 5).
 */
export function formatFreshnessLabel(listing = {}) {
  const { lastConfirmedAt, availabilityStatus } = listing;

  if (availabilityStatus === AVAILABILITY_STATUS.PENDING_CONFIRMATION) {
    return "Disponibilidad por confirmar";
  }

  if (lastConfirmedAt) {
    const ts = new Date(lastConfirmedAt).getTime();
    if (!Number.isNaN(ts)) {
      const ageDays = (Date.now() - ts) / (24 * 3_600_000);
      if (ageDays <= FRESHNESS_RECENT_DAYS) {
        return "Actualizado recientemente";
      }
    }
  }

  if (availabilityStatus === AVAILABILITY_STATUS.AVAILABLE) {
    return "Disponible";
  }

  if (!availabilityStatus || availabilityStatus === AVAILABILITY_STATUS.UNKNOWN) {
    return "Por confirmar";
  }

  return formatAvailabilityStatus(availabilityStatus);
}

/**
 * Compact specs row for cards, e.g. "2020 · 52.000 km · Automática · Gasolina".
 */
export function formatSpecsRow(listing = {}, unknownLabel = UNKNOWN_ATTRIBUTE_LABEL) {
  return [
    formatVehicleYear(listing.year, unknownLabel),
    formatMileageKmLong(listing.mileageKm, unknownLabel),
    formatTransmission(listing.transmission, unknownLabel),
    formatFuelType(listing.fuelType, unknownLabel),
  ].join(" · ");
}

export function formatFairPriceLabel(label) {
  if (!label || label === LISTING_FAIR_PRICE_LABEL.NOT_ENOUGH_DATA) {
    return LISTING_FALLBACK_COPY.fairPrice;
  }
  return formatFairPriceLabelFromVm(label);
}

export function formatRiskLevel(level) {
  if (!level || level === LISTING_RISK_LEVEL.UNKNOWN) {
    return LISTING_FALLBACK_COPY.risk;
  }
  return getRiskLevelLabel(level);
}

export function getDealerInitials(name) {
  const label = name || LISTING_FALLBACK_COPY.dealer;
  return label
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("") || "AM";
}

export function getVehicleListingInitials(listing = {}) {
  const make = String(listing.make || listing.makeNormalized || "").trim();
  const model = String(listing.model || listing.modelNormalized || "").trim();
  const parts = [make, model].filter(Boolean);

  if (parts.length) {
    return (
      parts
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() || "")
        .join("") || "AM"
    );
  }

  const title = formatVehicleTitle(listing, { fallback: "" });
  const titleWords = title
    .split(/\s+/)
    .filter(Boolean)
    .filter((word) => !/^\d{4}$/.test(word));
  return getDealerInitials(titleWords.join(" ")) || "AM";
}
